import * as THREE from 'three';
import { withFogOfWar } from '../environment/fog-of-war.js';

const VERTEX = /* glsl */ `
  #include <common>
  #include <fog_pars_vertex>
  attribute vec2 aCorner; attribute vec4 aPuff; attribute float aSize;
  varying vec2 vCorner; varying vec4 vPuff;
  void main() {
    vCorner = aCorner; vPuff = aPuff;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.);
    float turn = aPuff.z * 6.2832;
    mvPosition.xy += mat2(cos(turn), sin(turn), -sin(turn), cos(turn)) * aCorner * aSize;
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }`;
const FRAGMENT = /* glsl */ `
  #include <common>
  #include <fog_pars_fragment>
  uniform float uTime; uniform vec3 uCore; uniform vec3 uRim;
  varying vec2 vCorner; varying vec4 vPuff;
  float dsHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float dsNoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
    return mix(mix(dsHash(i), dsHash(i + vec2(1, 0)), f.x), mix(dsHash(i + vec2(0, 1)), dsHash(i + vec2(1, 1)), f.x), f.y);
  }
  void main() {
    // vPuff: x opacity, y how much of the cold rim shows, z its turn (the vertex shader's), w its seed.
    float r = length(vCorner);
    vec2 p = vCorner * 1.7 + vPuff.w * 17.;
    float n = .55 * dsNoise(p + uTime * .5) + .3 * dsNoise(p * 2.1 - uTime * .7) + .15 * dsNoise(p * 4.3);
    float body = (1. - smoothstep(.15, 1., r)) * smoothstep(.22, .62, n);
    float alpha = body * vPuff.x;
    if (alpha < .004) discard;
    // The smoke's torn edges catch a faint cold light; its heart is black.
    float edge = smoothstep(.35, .62, n) * (1. - smoothstep(.62, .85, n)) + smoothstep(.45, .95, r) * .6;
    gl_FragColor = vec4(mix(uCore, uRim, clamp(edge * vPuff.y, 0., 1.)), alpha);
    #include <fog_fragment>
  }`;

/**
 * A pool of black smoke puffs drawn as camera-facing cards in one draw, normally blended so they darken what is behind them (fire and
 * light are additive; this is the absence of light). The owner places each puff every frame with `set` and calls `commit`.
 */
export class DarkSmoke {
  readonly mesh: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  private centres: THREE.BufferAttribute; private puffs: THREE.BufferAttribute; private sizes: THREE.BufferAttribute;
  constructor(readonly count: number, core: number, rim: number, name: string) {
    const corners = new Float32Array(count * 8), index: number[] = [];
    for (let i = 0; i < count; i++) { corners.set([-1, -1, 1, -1, 1, 1, -1, 1], i * 8); index.push(i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3); }
    const geometry = new THREE.BufferGeometry();
    this.centres = new THREE.BufferAttribute(new Float32Array(count * 12), 3).setUsage(THREE.DynamicDrawUsage);
    this.puffs = new THREE.BufferAttribute(new Float32Array(count * 16), 4).setUsage(THREE.DynamicDrawUsage);
    this.sizes = new THREE.BufferAttribute(new Float32Array(count * 4), 1).setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('position', this.centres); geometry.setAttribute('aCorner', new THREE.BufferAttribute(corners, 2));
    geometry.setAttribute('aPuff', this.puffs); geometry.setAttribute('aSize', this.sizes); geometry.setIndex(index);
    const material = withFogOfWar(new THREE.ShaderMaterial({
      vertexShader: VERTEX, fragmentShader: FRAGMENT, transparent: true, depthWrite: false, fog: true,
      uniforms: { ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), uTime: { value: 0 }, uCore: { value: new THREE.Color(core) }, uRim: { value: new THREE.Color(rim) } },
    }));
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = name; this.mesh.frustumCulled = false; this.mesh.renderOrder = 3;
  }
  /** Puff `i` at a world point: `size` metres across its middle, `opacity` 0–1, `rim` how much cold edge light it shows, `turn` and `seed` 0–1. */
  set(i: number, x: number, y: number, z: number, size: number, opacity: number, rim = .5, turn = 0, seed = i * .618) {
    for (let k = 0; k < 4; k++) { this.centres.setXYZ(i * 4 + k, x, y, z); this.puffs.setXYZW(i * 4 + k, opacity, rim, turn, seed % 1); this.sizes.setX(i * 4 + k, size / 2); }
  }
  hide(i: number) { this.set(i, 0, -100, 0, 0, 0); }
  commit(time: number) {
    this.centres.needsUpdate = true; this.puffs.needsUpdate = true; this.sizes.needsUpdate = true;
    this.mesh.material.uniforms.uTime.value = time;
  }
  dispose() { this.mesh.removeFromParent(); this.mesh.geometry.dispose(); this.mesh.material.dispose(); }
}

let glow: THREE.DataTexture | undefined;
/** A soft round falloff, white at the middle, for the faint light round dark things (shared; never disposed). */
export function softGlowTexture() {
  if (glow) return glow;
  const size = 64, data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const r = Math.hypot(x + .5 - size / 2, y + .5 - size / 2) / (size / 2), a = Math.max(0, 1 - r) ** 2.2;
    data.set([255, 255, 255, Math.round(a * 255)], (y * size + x) * 4);
  }
  glow = new THREE.DataTexture(data, size, size); glow.needsUpdate = true; glow.magFilter = THREE.LinearFilter; glow.minFilter = THREE.LinearFilter;
  return glow;
}
